import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaHome,
  FaCar,
  FaBuilding,
  FaCarSide,
  FaExclamationTriangle,
  FaTags,
  FaIndustry,
  FaCogs,
  FaExclamationCircle,
  FaTools,
  FaCubes,
  FaRss,
  FaCalendarAlt
} from 'react-icons/fa';
import { applyThemePreference, getStoredThemePreference } from '../utils/theme';

const Sidebar = ({ isOpen = false, onClose, isCollapsed = false, onToggleCollapse, allowedPages, userRole: externalUserRole }) => {
    const location = useLocation();
    const touchStartXRef = useRef(null);
    const touchCurrentXRef = useRef(null);
    const sidebarRef = useRef(null);
  const [cachedRole] = useState(() => {
    try {
      const raw = localStorage.getItem('token');
      if (!raw) return 'bruger';
      // role kommer fra verify kaldet i App; vi kan også cache i localStorage
      const cached = localStorage.getItem('userRole');
      return cached || 'bruger';
    } catch (_) { return 'bruger'; }
  });
  const userRole = externalUserRole || cachedRole;
  const [theme, setTheme] = useState(() => getStoredThemePreference());
  const [themeHours, setThemeHours] = useState(() => {
    try { return JSON.parse(localStorage.getItem('themeHours') || 'null'); } catch (_) { return null; }
  });
  const [showOdinFeed, setShowOdinFeed] = useState(false);
  const [odinFeed, setOdinFeed] = useState({ title: '', description: '', items: [] });
  const [odinStatus, setOdinStatus] = useState('idle');
  const [odinError, setOdinError] = useState('');
  const [selectedOdinStation, setSelectedOdinStation] = useState('');
  const lastOdinFetchRef = useRef(0);
  const odinAbortRef = useRef(null);
  const [afspadseringNewCount, setAfspadseringNewCount] = useState(0);
  const [inventoryLowCount, setInventoryLowCount] = useState(0);
  const lastViewedRef = useRef(null);
  const [isInventoryOpen, setIsInventoryOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.startsWith('/lager') || window.location.pathname.startsWith('/indkoeb');
  });
  const inventoryPaths = useMemo(() => ['/lager', '/indkoeb'], []);

  useEffect(() => {
    const isInventoryPath = inventoryPaths.some((path) => location.pathname.startsWith(path));
    if (!isInventoryPath) {
      setIsInventoryOpen(false);
    }
  }, [inventoryPaths, location.pathname]);

  const authToken = useMemo(() => {
    try {
      return localStorage.getItem('token');
    } catch (_) {
      return null;
    }
  }, []);

  const getLastViewed = useCallback(() => {
    try {
      const raw = localStorage.getItem('afspadseringLastViewed');
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    } catch (_) {
      /* ignore */
    }
    return 0;
  }, []);

  const fetchAfspadseringNewCount = useCallback(async () => {
    if (userRole !== 'admin' || !authToken) return;
    const since = getLastViewed();
    if (since && since === lastViewedRef.current) {
      // avoid duplicate fetch with identical since
      return;
    }
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    try {
      const res = await fetch(`/api/afspadsering/admin/new-count?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setAfspadseringNewCount(Number(data?.count) || 0);
      if (data?.latestCreatedAt) {
        lastViewedRef.current = Number(data.latestCreatedAt);
      }
    } catch (_) {
      // ignore badge errors
    }
  }, [authToken, getLastViewed, userRole]);

  const fetchInventoryLowCount = useCallback(async () => {
    if (!authToken) return;
    try {
      const res = await fetch('/api/inventory/low-stock-count', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setInventoryLowCount(Number(data?.total) || 0);
    } catch (_) {
      // ignore badge errors
    }
  }, [authToken]);

  const hasPermission = (permissionId) => {
    if (!permissionId) return true;
    if (!allowedPages) return true;
    if (allowedPages instanceof Set) {
      return allowedPages.has(permissionId);
    }
    if (Array.isArray(allowedPages)) {
      return allowedPages.includes(permissionId);
    }
    return true;
  };

  // Hent theme hours én gang og hold opdateret
  useEffect(() => {
    fetch('/api/indstillinger/theme')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && typeof data.auto_start_hour === 'number' && typeof data.auto_end_hour === 'number') {
          setThemeHours(data);
          try { localStorage.setItem('themeHours', JSON.stringify(data)); } catch (_) {}
        }
      })
      .catch(() => {});
  }, []);

  // (Push-notifikationer midlertidigt fjernet)

  // Apply theme class to documentElement; auto er kun tidsstyret
  useEffect(() => {
    applyThemePreference(theme, themeHours);
  }, [theme, themeHours]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('theme-change', { detail: { theme } }));
  }, [theme]);

  // Re-evaluer auto-tema hver minut, så det skifter når grænsen passeres
  useEffect(() => {
    if (theme !== 'auto') return;
    const intervalId = setInterval(() => {
      applyThemePreference('auto', themeHours);
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [theme, themeHours]);

  useEffect(() => {
    const handleThemeChange = (event) => {
      const next = event?.detail?.theme || getStoredThemePreference();
      setTheme(next);
      applyThemePreference(next, themeHours);
    };
    window.addEventListener('theme-change', handleThemeChange);
    return () => window.removeEventListener('theme-change', handleThemeChange);
  }, [themeHours]);

  const fetchOdinFeed = useCallback(async ({ showLoading = true, allowCache = true } = {}) => {
    const now = Date.now();
    if (allowCache && odinStatus === 'loaded' && now - lastOdinFetchRef.current < 5 * 60 * 1000) {
      return;
    }

    if (odinAbortRef.current) {
      odinAbortRef.current.abort();
    }

    const controller = new AbortController();
    odinAbortRef.current = controller;

    if (showLoading) setOdinStatus('loading');
    setOdinError('');

    try {
      const res = await fetch('/api/rss/odin', { signal: controller.signal });
      if (!res.ok) {
        let message = 'Kunne ikke hente ODIN feedet';
        try {
          const errData = await res.json();
          if (errData?.message) message = errData.message;
        } catch (_) {}
        throw new Error(message);
      }

      const data = await res.json();
      setOdinFeed({
        title: data?.title || 'ODIN 1-1-2 puls',
        description: data?.description || '',
        items: Array.isArray(data?.items) ? data.items : []
      });
      setSelectedOdinStation('');
      setOdinStatus('loaded');
      lastOdinFetchRef.current = Date.now();
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setOdinStatus('error');
      setOdinError(err?.message || 'Der opstod en fejl ved hentning af feedet');
    }
  }, [odinStatus]);

  useEffect(() => {
    fetchOdinFeed({ showLoading: false, allowCache: false });
    return () => {
      if (odinAbortRef.current) {
        odinAbortRef.current.abort();
      }
    };
  }, [fetchOdinFeed]);

  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    fetchAfspadseringNewCount();
    const onFocus = () => fetchAfspadseringNewCount();
    const onViewed = () => fetchAfspadseringNewCount();
    const intervalId = setInterval(fetchAfspadseringNewCount, 60000);
    window.addEventListener('focus', onFocus);
    window.addEventListener('afspadsering-last-viewed', onViewed);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('afspadsering-last-viewed', onViewed);
    };
  }, [fetchAfspadseringNewCount, userRole]);

  useEffect(() => {
    fetchInventoryLowCount();
    const onFocus = () => fetchInventoryLowCount();
    const intervalId = setInterval(fetchInventoryLowCount, 60000);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchInventoryLowCount]);

  useEffect(() => {
    if (!showOdinFeed) return undefined;
    fetchOdinFeed({ showLoading: !odinFeed.items.length });
    return undefined;
  }, [fetchOdinFeed, odinFeed.items.length, showOdinFeed]);

  const parseOdinStation = useCallback((description = '') => {
    if (!description) return '';
    const normalized = description.replace(/<[^>]+>/g, '').trim();
    const stationMatch = normalized.match(/station:\s*([^\n<]+)$/i) || normalized.match(/station:\s*([^\n]+?)(?:\.|$)/i);
    if (!stationMatch || !stationMatch[1]) return '';
    return stationMatch[1].trim();
  }, []);

  const odinStations = useMemo(() => {
    const stationSet = new Set();
    if (Array.isArray(odinFeed.items)) {
      odinFeed.items.forEach((item) => {
        const station = parseOdinStation(item?.description || '');
        if (station) stationSet.add(station);
      });
    }
    return Array.from(stationSet).sort((a, b) => a.localeCompare(b, 'da'));
  }, [odinFeed.items, parseOdinStation]);

  const filteredOdinItems = useMemo(() => {
    if (!Array.isArray(odinFeed.items)) return [];
    if (!selectedOdinStation) return odinFeed.items;
    const target = selectedOdinStation.toLowerCase();
    return odinFeed.items.filter((item) => {
      const station = parseOdinStation(item?.description || '');
      return station && station.toLowerCase() === target;
    });
  }, [odinFeed.items, parseOdinStation, selectedOdinStation]);

  const formatPubDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

    const menuItems = [
      {
        path: '/dashboard',
        icon: FaHome,
        label: 'Dashboard',
        exact: true,
        permissionId: 'dashboard'
      },
      {
        path: '/biler',
        icon: FaCar,
        label: 'Biler',
        permissionId: 'biler'
      },
      {
        path: '/stationer',
        icon: FaBuilding,
        label: 'Stationer',
        permissionId: 'stationer'
      },
      {
        path: '/laanebiler',
        icon: FaCarSide,
        label: 'Lånebiler',
        permissionId: 'laanebiler'
      },
      {
        path: '/fejlmeldinger',
        icon: FaExclamationTriangle,
        label: 'Fejlmeldinger',
        permissionId: 'fejlmeldinger'
      },
      {
        path: '/3d-priser',
        icon: FaCubes,
        label: '3D priser',
        permissionId: 'print_calculator'
      },
      // Admin-only entries (tilføjes betinget nedenfor)
    ];

  const adminMenuItems = [
    {
      name: 'vaerksted',
      icon: FaIndustry,
      label: 'Værksted',
      path: '/vaerksted',
      permissionId: 'vaerksted'
    }
  ];

  const afspadseringMenuItem = {
    path: '/afspadsering',
    icon: FaCalendarAlt,
    label: 'Afspadsering',
    permissionId: 'afspadsering'
  };

  // Byg endelig menu: skjul Salg og Indstillinger for ikke-admin
  const finalMenuItems = React.useMemo(() => {
    const base = [...menuItems];
    if (userRole === 'admin') {
      base.push({ path: '/salg', icon: FaTags, label: 'Salg', permissionId: 'salg' });
    }
    return base;
  }, [userRole]);

  const settingsMenuItem = userRole === 'admin'
    ? { path: '/indstillinger', icon: FaCogs, label: 'Indstillinger', permissionId: 'indstillinger' }
    : null;

  const inventoryItems = [
    { path: '/lager/pluk', icon: FaTools, label: 'Pluk', permissionId: 'inventory_pick' },
    { path: '/indkoeb/ordrer', icon: FaTags, label: 'Salg', permissionId: 'orders' },
    { path: '/lager/varer', icon: FaCubes, label: 'Varer', permissionId: 'inventory_items' }
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const inventoryVisible = inventoryItems.some((item) => hasPermission(item.permissionId));
  const isInventoryActive = inventoryItems.some((item) => isActive(item.path));

  const sidebarWidthClass = isCollapsed ? 'w-16' : 'w-64';
  const labelVisibility = isCollapsed ? 'hidden' : 'inline';

    useEffect(() => {
      const sidebar = sidebarRef.current;
      if (!sidebar) return undefined;

      const resetTouchState = () => {
        touchStartXRef.current = null;
        touchCurrentXRef.current = null;
      };

      const handleTouchStart = (event) => {
        if (!isOpen) return;
        const touch = event.touches[0];
        touchStartXRef.current = touch.clientX;
        touchCurrentXRef.current = touch.clientX;
      };

      const handleTouchMove = (event) => {
        if (touchStartXRef.current === null) return;
        const touch = event.touches[0];
        touchCurrentXRef.current = touch.clientX;

        const deltaX = touchCurrentXRef.current - touchStartXRef.current;
        if (deltaX < 0) {
          const translateX = Math.max(deltaX, -sidebar.offsetWidth);
          sidebar.style.transform = `translateX(${translateX}px)`;
        }
      };

      const handleTouchEnd = () => {
        if (touchStartXRef.current === null || touchCurrentXRef.current === null) {
          sidebar.style.transform = '';
          resetTouchState();
          return;
        }

        const deltaX = touchCurrentXRef.current - touchStartXRef.current;
        sidebar.style.transform = '';

        const threshold = sidebar.offsetWidth * 0.35;
        if (deltaX < -threshold) {
          onClose?.();
        }

        resetTouchState();
      };

      sidebar.addEventListener('touchstart', handleTouchStart, { passive: true });
      sidebar.addEventListener('touchmove', handleTouchMove, { passive: true });
      sidebar.addEventListener('touchend', handleTouchEnd, { passive: true });
      sidebar.addEventListener('touchcancel', handleTouchEnd, { passive: true });

      if (!isOpen) {
        sidebar.style.transform = '';
        resetTouchState();
      }

      return () => {
        sidebar.removeEventListener('touchstart', handleTouchStart);
        sidebar.removeEventListener('touchmove', handleTouchMove);
        sidebar.removeEventListener('touchend', handleTouchEnd);
        sidebar.removeEventListener('touchcancel', handleTouchEnd);
        sidebar.style.transform = '';
        resetTouchState();
      };
    }, [isOpen, onClose]);

    return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`fixed inset-0 bg-black/40 z-30 lg:hidden transition-opacity ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      <aside
          ref={sidebarRef}
        className={`bg-gray-800 dark:bg-gray-900 text-white ${sidebarWidthClass} min-h-screen flex flex-col fixed z-40 lg:static transform transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
      {/* Logo og navigation */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/20">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-xl overflow-hidden ring-2 ring-white/60 dark:ring-white/20 bg-white shadow">
              <img src="/favicon.svg" alt="Falck CV" className="h-full w-full object-cover" />
            </div>
            <span className={`font-bold text-lg text-gray-100 ${isCollapsed ? 'hidden' : 'inline'}`}>Falck CV</span>
          </div>
          <button
            onClick={onToggleCollapse}
            className="hidden lg:inline-flex text-gray-300 hover:text-white px-2 py-1 rounded"
            aria-label="Toggle sidebar width"
          >
            {isCollapsed ? '»' : '«'}
          </button>
        </div>

        <nav className="flex-1 p-2 lg:p-4">
        <ul className="space-y-2">
          {finalMenuItems.filter(item => hasPermission(item.permissionId)).map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`flex items-center ${isCollapsed ? 'justify-center space-x-0 px-2 lg:px-2' : 'space-x-3 px-3 lg:px-4'} py-2 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-200 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={onClose}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className={`${labelVisibility}`}>{item.label}</span>
              </Link>
            </li>
          ))}

          {inventoryVisible && (
            <li>
              <button
                type="button"
                onClick={() => setIsInventoryOpen((prev) => !prev)}
                className={`w-full flex items-center justify-between ${isCollapsed ? 'px-2 lg:px-2' : 'px-3 lg:px-4'} py-2 rounded-lg transition-colors ${
                  isInventoryActive ? 'bg-gray-700 text-white' : 'text-gray-200 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <span className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'space-x-3'}`}>
                  <FaCubes className="w-5 h-5 flex-shrink-0" />
                  <span className={`${labelVisibility}`}>Lager</span>
                  {inventoryLowCount > 0 && (
                    <span
                      className={`${isCollapsed ? 'ml-2' : 'ml-0'} inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-semibold min-w-[20px] h-5 px-2`}
                      aria-label={`Varer under minimum: ${inventoryLowCount}`}
                    >
                      {inventoryLowCount}
                    </span>
                  )}
                </span>
                {!isCollapsed && (
                  <span className="text-xs">{isInventoryOpen ? '▾' : '▸'}</span>
                )}
              </button>
              {isInventoryOpen && !isCollapsed && (
                <ul className="mt-1 space-y-1 pl-8">
                  {inventoryItems.filter(item => hasPermission(item.permissionId)).map((item) => (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`flex items-center space-x-2 px-3 py-1 rounded-lg transition-colors ${
                          isActive(item.path) ? 'bg-gray-700 text-white' : 'text-gray-200 hover:bg-gray-700 hover:text-white'
                        }`}
                        onClick={onClose}
                      >
                        <item.icon className="w-4 h-4 flex-shrink-0" />
                        <span className={`${labelVisibility}`}>{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}

          {/* Admin Menu Items */}
          {userRole === 'admin' && adminMenuItems.filter(menuItem => hasPermission(menuItem.permissionId)).map((menuItem) => (
            <li key={menuItem.name}>
              <Link
                to={menuItem.path}
                className={`flex items-center ${isCollapsed ? 'justify-center space-x-0 px-2 lg:px-2' : 'space-x-3 px-3 lg:px-4'} py-2 rounded-lg transition-colors text-gray-200 hover:bg-gray-700 hover:text-white`}
                onClick={onClose}
              >
                <menuItem.icon className="w-5 h-5 flex-shrink-0" />
                <span className={`${labelVisibility}`}>{menuItem.label}</span>
              </Link>
            </li>
          ))}

          {hasPermission(afspadseringMenuItem.permissionId) && (
            <li key={afspadseringMenuItem.path}>
              <Link
                to={afspadseringMenuItem.path}
                className={`relative flex items-center ${isCollapsed ? 'justify-center space-x-0 px-2 lg:px-2' : 'space-x-3 px-3 lg:px-4'} py-2 rounded-lg transition-colors ${
                  isActive(afspadseringMenuItem.path, afspadseringMenuItem.exact)
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-200 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={onClose}
              >
                <afspadseringMenuItem.icon className="w-5 h-5 flex-shrink-0" />
                <span className={`${labelVisibility}`}>{afspadseringMenuItem.label}</span>
                {userRole === 'admin' && afspadseringNewCount > 0 && (
                  <span
                    className={`${isCollapsed ? 'ml-2' : 'ml-auto'} inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-semibold min-w-[20px] h-5 px-2`}
                    aria-label={`Nye afspadseringer: ${afspadseringNewCount}`}
                  >
                    {afspadseringNewCount}
                  </span>
                )}
              </Link>
            </li>
          )}

          {settingsMenuItem && hasPermission(settingsMenuItem.permissionId) && (
            <li key={settingsMenuItem.path} className="pt-4 mt-4 border-t border-gray-700/50">
              <Link
                to={settingsMenuItem.path}
                className={`flex items-center ${isCollapsed ? 'justify-center space-x-0 px-2 lg:px-2' : 'space-x-3 px-3 lg:px-4'} py-2 rounded-lg transition-colors text-gray-200 hover:bg-gray-700 hover:text-white`}
                onClick={onClose}
              >
                <settingsMenuItem.icon className="w-5 h-5 flex-shrink-0" />
                <span className={`${labelVisibility}`}>{settingsMenuItem.label}</span>
              </Link>
            </li>
          )}
        </ul>
        </nav>

        {/* Theme toggle + Close button (bottom) */}
        <div className="mt-auto p-2 border-t border-gray-700/50 space-y-2">
          <div>
            <button
              onClick={() => setShowOdinFeed(true)}
              className="w-full bg-amber-500/80 hover:bg-amber-500 text-white py-2 rounded flex items-center justify-center space-x-2 text-sm"
            >
              <FaRss className="w-4 h-4" />
              {!isCollapsed && <span>ODIN 1-1-2 puls</span>}
            </button>
          </div>
          {!isCollapsed && (
            <div className="flex items-center justify-between">
              <span className="text-gray-300">Tema</span>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                className="bg-gray-700 dark:bg-gray-800 text-white text-sm rounded px-2 py-1"
              >
                <option value="auto">Auto</option>
                <option value="light">Lys</option>
                <option value="dark">Mørk</option>
              </select>
            </div>
          )}
          {/* Push-notifikationer fjernet midlertidigt */}
          <div className="lg:hidden">
            <button onClick={onClose} className="w-full bg-gray-700 dark:bg-gray-800 text-white py-2 rounded">Luk</button>
          </div>
        </div>
      </aside>
      {showOdinFeed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowOdinFeed(false)} />
          <div
            className="relative bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg shadow-xl w-11/12 max-w-3xl max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{odinFeed.description || 'Seneste alarmer'}</p>
                <h3 className="text-lg font-semibold">{odinFeed.title || 'ODIN 1-1-2 puls'}</h3>
              </div>
              <button
                aria-label="Luk ODIN feed"
                className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
                onClick={() => setShowOdinFeed(false)}
              >
                ×
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh] space-y-4">
              {odinStatus === 'loading' && <p className="text-sm text-gray-600 dark:text-gray-300">Henter alarmer...</p>}
              {odinStatus === 'error' && (
                <p className="text-sm text-red-600 dark:text-red-400">{odinError || 'Kunne ikke hente feedet.'}</p>
              )}
              {odinStatus === 'loaded' && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Filtrer på station</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Matcher "Station: NAVN" i slutningen af alarmen.</p>
                  </div>
                  <select
                    className="w-full sm:w-64 bg-gray-50 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-700 text-sm rounded px-3 py-2 text-gray-900 dark:text-gray-100"
                    value={selectedOdinStation}
                    onChange={(e) => setSelectedOdinStation(e.target.value)}
                  >
                    <option value="">Alle stationer</option>
                    {odinStations.map((station) => (
                      <option key={station} value={station}>{station}</option>
                    ))}
                  </select>
                </div>
              )}
              {odinStatus === 'loaded' && filteredOdinItems.length === 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-300">Ingen alarmer fundet for det valgte filter.</p>
              )}
              {odinStatus === 'loaded' && filteredOdinItems.length > 0 && (
                <ul className="space-y-3">
                  {filteredOdinItems.map((item, idx) => (
                    <li
                      key={`${item.title}-${item.pubDate}-${idx}`}
                      className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-900/70"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{formatPubDate(item.pubDate)}</p>
                          <p className="font-semibold text-gray-900 dark:text-gray-100">{item.title}</p>
                        </div>
                        {item.comments && (
                          <span className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{item.comments}</span>
                        )}
                      </div>
                      {item.description && (
                        <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{item.description}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;

// Helper: VAPID public key base64 -> Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
